/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { FetchBaseQueryError } from "@reduxjs/toolkit/query/react";

/**
 * Type predicate to narrow an unknown error to `FetchBaseQueryError`
 */
export const isFetchBaseQueryError = (
  error: unknown
): error is FetchBaseQueryError => {
  return typeof error === "object" && error !== null && "status" in error;
};

/**
 * Whether a request failed because of the network, a timeout or a server error
 */
export const isConnectionError = (error: unknown): boolean => {
  if (!isFetchBaseQueryError(error)) {
    return (
      typeof error === "object" &&
      error !== null &&
      "code" in error &&
      error.code === "auth/network-request-failed"
    );
  }
  switch (error.status) {
    case "FETCH_ERROR":
    case "TIMEOUT_ERROR":
      return true;
    case "PARSING_ERROR":
      return error.originalStatus >= 500;
    case "CUSTOM_ERROR":
      return false;
    default:
      return error.status >= 500;
  }
};

/**
 * The backend's `detail` message for a failed request, or null if it carries none
 */
export const fetchErrorDetail = (error: unknown): string | null => {
  if (
    isFetchBaseQueryError(error) &&
    error.data &&
    typeof error.data === "object" &&
    "detail" in error.data &&
    typeof error.data.detail === "string"
  ) {
    return error.data.detail;
  }
  return null;
};

export const createMapping = <Data, Key extends string | number>(
  items: Data[],
  key: (item: Data) => Key
): Record<Key, Data> => {
  return Object.fromEntries(items.map((item) => [key(item), item])) as Record<
    Key,
    Data
  >;
};

export const createNestedMapping = <Data,>(
  items: Data[],
  outerKey: (item: Data) => number,
  innerKey: (item: Data) => number
) => {
  const result: Record<number, Record<number, Data>> = {};

  for (const item of items) {
    const itemOuterKey = outerKey(item);
    const itemInnerKey = innerKey(item);

    if (!(itemOuterKey in result)) {
      result[itemOuterKey] = {};
    }
    const outerRecord = result[itemOuterKey];
    if (outerRecord) {
      outerRecord[itemInnerKey] = item;
    }
  }

  return result;
};

export type ElementName =
  "copper" | "chromium" | "molybdenum" | "nickel" | "tin";

export type Variability = "low" | "medium" | "high";

export const elementVariability = (
  element: ElementName,
  scale: number
): Variability | "none" => {
  const lowVariability: Record<ElementName, number> = {
    copper: 0.02,
    chromium: 0.02,
    molybdenum: 0.002,
    nickel: 0.02,
    tin: 0.002,
  };
  const mediumVariability: Record<ElementName, number> = {
    copper: 0.1,
    chromium: 0.1,
    molybdenum: 0.01,
    nickel: 0.1,
    tin: 0.01,
  };

  if (scale <= 0) {
    return "none";
  } else if (scale < lowVariability[element]) {
    return "low";
  } else if (scale < mediumVariability[element]) {
    return "medium";
  } else {
    return "high";
  }
};

export const groupBy = <T, K extends string | number, A>(
  items: T[],
  key: (item: T) => K,
  aggregate: (items: T[]) => A
): K extends string ? Record<string, A> : Record<number, A> => {
  const result: Record<string | number, T[]> = {};

  items.forEach((item) => {
    const itemKey = key(item);
    if (!result[itemKey]) {
      result[itemKey] = [];
    }
    result[itemKey].push(item);
  });

  return Object.fromEntries(
    Object.entries(result).map(([key, value]) => [key, aggregate(value)])
  );
};

export const getBase64 = (file: File) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      let result = reader.result;
      if (typeof result === "string") {
        result = result.split(",")[1] ?? "";
      }
      resolve(result);
    };
    reader.onerror = (error: unknown) => {
      reject(error instanceof Error ? error : new Error(String(error)));
    };
  });
};

export const scaleToHeats = (
  value: number | null | undefined,
  targetNumHeats: number | null | undefined,
  originalNumHeats: number | null | undefined
) =>
  value == null || targetNumHeats == null || originalNumHeats == null
    ? undefined
    : originalNumHeats > 0.0
      ? (value * targetNumHeats) / originalNumHeats
      : 0.0;

export interface EntityMapping<T> {
  byIndex: T[];
  byId: Record<number, T>;
  byName: Record<string, T>;
}

/**
 * Split an array's items based on whether the predicate is true for each one.
 */
export const partition = <T,>(
  predicate: (item: T) => boolean,
  items: T[]
): [T[], T[]] => {
  return [items.filter(predicate), items.filter((item) => !predicate(item))];
};

export const sorted = <T,>(
  items: T[],
  key: (item: T) => number | boolean | string | number[],
  reverse?: boolean
): T[] => {
  return [...items].sort((left, right) => {
    const leftKey = key(left);
    const rightKey = key(right);

    if (typeof leftKey === "string" && typeof rightKey === "string") {
      const dir = leftKey.localeCompare(rightKey);
      return reverse ? -dir : dir;
    } else if (Array.isArray(leftKey) && Array.isArray(rightKey)) {
      const diffIndex = leftKey.findIndex((val, i) => val !== rightKey[i]);

      const l = leftKey[diffIndex];
      if (diffIndex !== -1 && l !== undefined) {
        const r = rightKey[diffIndex];

        // If r is undefined, right array is shorter, so right < left
        if (r === undefined) {
          return reverse ? -1 : 1;
        }
        const dir = l < r ? -1 : 1;
        return reverse ? -dir : dir;
      }

      const dir = leftKey.length < rightKey.length ? -1 : 0;
      return reverse ? -dir : dir;
    } else {
      const dir = leftKey < rightKey ? -1 : leftKey > rightKey ? 1 : 0;
      return reverse ? -dir : dir;
    }
  });
};

export const unique = <T,>(items: Iterable<T>): T[] =>
  Array.from(new Set(items));

export const toggleMember = <T,>(
  items: ReadonlySet<T>,
  item: T
): ReadonlySet<T> =>
  items.has(item)
    ? new Set([...items].filter((member) => member !== item))
    : new Set([...items, item]);

/** Type-level check that a readonly string array exactly covers a string union. */
export type ExactStringUnionArray<
  Arr extends readonly string[],
  U extends string,
> = [U] extends [Arr[number]]
  ? [Arr[number]] extends [U]
    ? true
    : "ERROR: Array has a string not in type"
  : "ERROR: Type has a string not in Array";

export const sum = (values: number[]) =>
  values.reduce((left, right) => left + right, 0);

export function sumBy<T>(values: T[], key: (value: T) => number) {
  return values.map(key).reduce((left, right) => left + right, 0);
}

export const minBy = <T,>(
  values: T[],
  key: (value: T) => number
): T | undefined =>
  values.reduce<T | undefined>(
    (smallest, value) =>
      smallest === undefined || key(value) < key(smallest) ? value : smallest,
    undefined
  );

export const maxBy = <T,>(
  values: T[],
  key: (value: T) => number
): T | undefined =>
  values.reduce<T | undefined>(
    (largest, value) =>
      largest === undefined || key(value) > key(largest) ? value : largest,
    undefined
  );
